/**
 * « Séances en direct »: the trainer plans a session; the server creates the video link in the
 * background (Google Meet when the agency set it up in Settings → Integrations, otherwise Jitsi
 * Meet), reminds the registered learners on WhatsApp and gives them the « Join » button.
 * Here: creation, list, registrations and attendance, and the agency settings (delays, Jitsi).
 */

import {
  type LiveProviderEnum,
  type LiveRegistration,
  type LiveSession,
  type LiveSessionRequest,
  type LiveSessionStatusEnum,
  useAcademyLiveSessionsAttendanceCreate,
  useAcademyLiveSessionsCreate,
  useAcademyLiveSessionsList,
  useAcademyLiveSessionsPartialUpdate,
  useAcademyLiveSessionsRegenerateLinkCreate,
  useAcademyLiveSessionsRegistrationsList,
  useAcademyLiveSettingsList,
  useAcademyLiveSettingsPartialUpdate,
} from "@agencyos/api-client";
import { doualaToIso, liveWhen, parseReminderMinutes } from "@agencyos/core";
import { Badge, type BadgeTone, Button, GlassCard, GlassSheet, SelectField, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { Link2, Plus, RefreshCw } from "lucide-react";
import { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { useAdminExam } from "./examContext";
import { ErrorList, isObject, Pager, useAcademyRoles, useRefreshAcademy } from "./shared";

const SKILLS = ["co", "ce", "ee", "eo"] as const;
const PROVIDERS: LiveProviderEnum[] = ["google_meet", "jitsi"];
const STATUS_TONE: Record<LiveSessionStatusEnum, BadgeTone> = { planned: "offer", cancelled: "danger", done: "success" };

function SessionStatus({ status }: { status: LiveSessionStatusEnum | undefined }) {
  const { t } = useTranslation();
  const value = status ?? "planned";
  return <Badge tone={STATUS_TONE[value]}>{t(`academyLive.admin.status.${value}`)}</Badge>;
}

function CreateSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshAcademy();
  const create = useAcademyLiveSessionsCreate({ mutation: { onSuccess: refresh } });
  const { params } = useAdminExam();
  const [form, setForm] = useState({ title: "", description: "", startsAt: "", duration: "60", skill: "", theme: "", capacity: "20", provider: "" });
  const [localError, setLocalError] = useState("");
  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));
  const errors = fieldErrors(create.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const startsAt = doualaToIso(form.startsAt);
    if (!startsAt) {
      setLocalError(t("academyLive.admin.badDate"));
      return;
    }
    setLocalError("");
    create.mutate(
      {
        data: {
          title: form.title.trim(),
          description: form.description.trim(),
          starts_at: startsAt,
          duration_minutes: Number(form.duration) || 0,
          skill: form.skill as LiveSessionRequest["skill"],
          theme: form.theme.trim(),
          capacity: Number(form.capacity) || 0,
          ...params,
          ...(form.provider ? { provider: form.provider as LiveProviderEnum } : {}),
        },
      },
      { onSuccess: (r) => r.status === 201 && onClose() },
    );
  };

  return (
    <GlassSheet open title={t("academyLive.admin.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("academyLive.admin.form")}>
        <p className="muted">{t("academyLive.admin.linkHint")}</p>
        <TextField label={t("academyLive.admin.sessionTitle")} value={form.title} maxLength={160} error={errors.title} onChange={(e) => set("title", e.target.value)} />
        <TextAreaField label={t("academyLive.admin.description")} rows={3} value={form.description} error={errors.description} onChange={(e) => set("description", e.target.value)} />
        <div className="form-grid">
          <TextField
            type="datetime-local"
            label={t("academyLive.admin.startsAt")}
            hint={t("academyLive.admin.timeZone")}
            value={form.startsAt}
            error={localError || errors.starts_at}
            onChange={(e) => set("startsAt", e.target.value)}
          />
          <TextField type="number" min={5} max={600} label={t("academyLive.admin.duration")} value={form.duration} error={errors.duration_minutes} onChange={(e) => set("duration", e.target.value)} />
          <TextField type="number" min={1} label={t("academyLive.admin.capacity")} value={form.capacity} error={errors.capacity} onChange={(e) => set("capacity", e.target.value)} />
          <SelectField
            label={t("academyLive.admin.skill")}
            value={form.skill}
            options={[{ value: "", label: t("academyLive.admin.anySkill") }, ...SKILLS.map((s) => ({ value: s, label: t(`academyLive.skill.${s}`) }))]}
            onChange={(e) => set("skill", e.target.value)}
          />
          <SelectField
            label={t("academyLive.admin.provider")}
            value={form.provider}
            options={[{ value: "", label: t("academyLive.admin.defaultProvider") }, ...PROVIDERS.map((p) => ({ value: p, label: t(`academyLive.provider.${p}`) }))]}
            onChange={(e) => set("provider", e.target.value)}
          />
        </div>
        <TextField label={t("academyLive.admin.theme")} lang="fr" value={form.theme} maxLength={160} error={errors.theme} onChange={(e) => set("theme", e.target.value)} />
        {create.error && !Object.keys(errors).length ? <ErrorList error={create.error} /> : null}
        <div className="row">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t("common.loading") : t("academyLive.admin.create")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function Attendees({ session, canEdit }: { session: LiveSession; canEdit: boolean }) {
  const { t } = useTranslation();
  const list = useAcademyLiveSessionsRegistrationsList(session.id);
  const mark = useAcademyLiveSessionsAttendanceCreate({ mutation: { onSuccess: () => void list.refetch() } });
  const rows: LiveRegistration[] = list.data?.status === 200 ? list.data.data : [];
  if (list.isPending) return <Skeleton style={{ height: 80 }} />;
  if (!rows.length) return <p className="muted">{t("academyLive.admin.noAttendees")}</p>;
  return (
    <ul className="plain-list stack" aria-label={t("academyLive.admin.attendees")}>
      {rows.map((r) => (
        <li key={r.id} className="row between">
          <span>
            {r.learner_name} <span className="muted academy-small">{r.learner_email}</span>
          </span>
          <SoftToggle
            label={t("academyLive.admin.presentOf", { name: r.learner_name })}
            checked={Boolean(r.present)}
            disabled={!canEdit || mark.isPending}
            onChange={(present) => mark.mutate({ id: session.id, data: { registration: r.id, present } })}
          />
        </li>
      ))}
    </ul>
  );
}

function reminderList(value: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (!isObject(value)) return "";
  return Object.keys(value)
    .map(Number)
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => b - a)
    .map((n) => t("academyLive.admin.minutes", { n }))
    .join(", ");
}

function SessionSheet({ session, onClose }: { session: LiveSession; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const update = useAcademyLiveSessionsPartialUpdate({ mutation: { onSuccess: refresh } });
  const regenerate = useAcademyLiveSessionsRegenerateLinkCreate({ mutation: { onSuccess: refresh } });
  const [current, setCurrent] = useState(session);
  const when = liveWhen(current.starts_at, i18n.language);
  const reminders = reminderList(current.reminders_sent, t);
  const setStatus = (status: LiveSessionStatusEnum) =>
    update.mutate({ id: current.id, data: { status } }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });

  return (
    <GlassSheet open title={current.title} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <SessionStatus status={current.status} />
          <Badge>{t(`academyLive.provider.${current.provider ?? "google_meet"}`)}</Badge>
          {current.skill ? <Badge>{t(`academyLive.skill.${current.skill}`)}</Badge> : null}
        </div>
        <p>{t("academyLive.when", { date: when.date, time: when.time, minutes: current.duration_minutes ?? 60 })}</p>
        {current.trainer_name ? <p className="muted">{t("academyLive.trainer", { name: current.trainer_name })}</p> : null}
        {current.join_url ? (
          <p>
            <a href={current.join_url} target="_blank" rel="noopener noreferrer" className="link">
              <Link2 aria-hidden size={16} /> {t("academyLive.admin.openLink")}
            </a>
          </p>
        ) : (
          <p className="muted" role="status">
            {t("academyLive.admin.linkPending")}
          </p>
        )}
        {current.link_note ? (
          <p className="muted academy-small" role="note">
            {current.link_note}
          </p>
        ) : null}
        <p className="muted academy-small">{reminders ? t("academyLive.admin.reminders", { list: reminders }) : t("academyLive.admin.noReminders")}</p>
        {canEdit ? (
          <div className="row">
            <Button variant="secondary" icon={<RefreshCw aria-hidden size={16} />} disabled={regenerate.isPending} onClick={() => regenerate.mutate({ id: current.id })}>
              {t("academyLive.admin.regenerate")}
            </Button>
            {current.status === "planned" ? (
              <>
                <Button variant="secondary" disabled={update.isPending} onClick={() => setStatus("done")}>
                  {t("academyLive.admin.markDone")}
                </Button>
                <Button variant="ghost" disabled={update.isPending} onClick={() => setStatus("cancelled")}>
                  {t("academyLive.admin.cancel")}
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
        <ErrorList error={update.error ?? regenerate.error} />
        <h3>{t("academyLive.admin.attendees")}</h3>
        <Attendees session={current} canEdit={canEdit} />
      </div>
    </GlassSheet>
  );
}

function LiveSettingsCard() {
  const { t } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const list = useAcademyLiveSettingsList();
  const row = list.data?.status === 200 ? list.data.data.results[0] : undefined;
  const save = useAcademyLiveSettingsPartialUpdate({ mutation: { onSuccess: () => void list.refetch() } });
  const [draft, setDraft] = useState<{ domain: string; reminders: string; opens: string; provider: string } | null>(null);
  const errors = fieldErrors(save.error);
  const [localError, setLocalError] = useState("");
  if (!row) return list.isPending ? <Skeleton style={{ height: 120 }} /> : null;
  const form = draft ?? {
    domain: row.jitsi_domain ?? "",
    reminders: Array.isArray(row.reminder_minutes) ? (row.reminder_minutes as unknown[]).join(", ") : "",
    opens: String(row.join_opens_minutes ?? 15),
    provider: row.default_provider ?? "google_meet",
  };
  const set = (key: keyof typeof form, value: string) => setDraft({ ...form, [key]: value });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const reminders = parseReminderMinutes(form.reminders);
    if (reminders === null) {
      setLocalError(t("academyLive.admin.settings.badReminders"));
      return;
    }
    setLocalError("");
    save.mutate(
      {
        id: row.id,
        data: { jitsi_domain: form.domain.trim(), reminder_minutes: reminders, join_opens_minutes: Number(form.opens) || 0, default_provider: form.provider as LiveProviderEnum },
      },
      { onSuccess: (r) => r.status === 200 && setDraft(null) },
    );
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="live-settings-title">
      <h3 id="live-settings-title">{t("academyLive.admin.settings.title")}</h3>
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("academyLive.admin.settings.title")}>
        <fieldset className="plain-fieldset form-grid" disabled={!canEditSettings}>
          <SelectField
            label={t("academyLive.admin.settings.provider")}
            value={form.provider}
            options={PROVIDERS.map((p) => ({ value: p, label: t(`academyLive.provider.${p}`) }))}
            onChange={(e) => set("provider", e.target.value)}
          />
          <TextField label={t("academyLive.admin.settings.jitsi")} hint={t("academyLive.admin.settings.jitsiHint")} value={form.domain} error={errors.jitsi_domain} onChange={(e) => set("domain", e.target.value)} />
          <TextField
            label={t("academyLive.admin.settings.reminders")}
            hint={t("academyLive.admin.settings.remindersHint")}
            value={form.reminders}
            error={localError || errors.reminder_minutes}
            onChange={(e) => set("reminders", e.target.value)}
          />
          <TextField type="number" min={0} max={240} label={t("academyLive.admin.settings.opens")} value={form.opens} error={errors.join_opens_minutes} onChange={(e) => set("opens", e.target.value)} />
        </fieldset>
        {canEditSettings ? (
          <div className="row">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t("common.loading") : t("academyLive.admin.settings.save")}
            </Button>
            {save.isSuccess && !draft ? <span role="status">{t("academyLive.admin.settings.saved")}</span> : null}
          </div>
        ) : null}
      </form>
    </GlassCard>
  );
}

export function LiveTab() {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<LiveSession | null>(null);
  const closeCreate = useCallback(() => setCreating(false), []);
  const closeOpen = useCallback(() => setOpen(null), []);
  const { params: examFilter } = useAdminExam();
  const list = useAcademyLiveSessionsList({ page, ...examFilter }, { query: { refetchInterval: 15_000 } });
  const data = list.data?.status === 200 ? list.data.data : undefined;

  return (
    <div className="stack">
      <GlassCard as="section" className="stack" aria-labelledby="live-title">
        <div className="section-title">
          <div>
            <h2 id="live-title">{t("academyLive.admin.title")}</h2>
            <p className="muted">{t("academyLive.admin.intro")}</p>
          </div>
          {canEdit ? (
            <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
              {t("academyLive.admin.new")}
            </Button>
          ) : null}
        </div>
        {data === undefined ? (
          <Skeleton style={{ height: 160 }} />
        ) : data.results.length ? (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("academyLive.admin.sessionTitle")}</th>
                    <th>{t("academyLive.admin.startsAt")}</th>
                    <th>{t("academyLive.admin.provider")}</th>
                    <th>{t("academyLive.admin.statusLabel")}</th>
                    <th>{t("academyLive.admin.registeredLabel")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((s) => {
                    const when = liveWhen(s.starts_at, i18n.language);
                    return (
                      <tr key={s.id}>
                        <td>
                          <button type="button" className="link" onClick={() => setOpen(s)}>
                            {s.title}
                          </button>
                        </td>
                        <td className="nowrap">{t("academyLive.admin.dateTime", { date: when.date, time: when.time })}</td>
                        <td>{s.join_url ? t(`academyLive.provider.${s.provider ?? "google_meet"}`) : t("academyLive.admin.linkPending")}</td>
                        <td>
                          <SessionStatus status={s.status} />
                        </td>
                        <td className="num">{t("academyLive.admin.registered", { n: s.registered_count, capacity: s.capacity ?? 0 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
          </>
        ) : (
          <p className="muted">{t("academyLive.admin.empty")}</p>
        )}
      </GlassCard>
      <LiveSettingsCard />
      {creating ? <CreateSheet onClose={closeCreate} /> : null}
      {open ? <SessionSheet key={open.id} session={open} onClose={closeOpen} /> : null}
    </div>
  );
}
